import React from "react";
import { useLocation } from "react-router-dom";

import { UserRolesEnum } from "../constants/UserRolesEnum";

const convertNumber = (number, withDecimal) => {
  number = withDecimal
    ? number >= 1000 && number < 1000000
      ? (number / 1000).toFixed(2) + "K"
      : number >= 1000000
      ? (number / 1000000).toFixed(2) + "M"
      : number
    : number >= 1000 && number < 1000000
    ? number / 1000 + "K"
    : number >= 1000000
    ? number / 1000000 + "M"
    : number;

  return number;
};

export { convertNumber };

export const serializeQueryParams = (params = {}) => {
  if (typeof params !== "object") return "";
  const query = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    Array.isArray(params[key])
      ? Array.from(params[key]).forEach((value) => query.append(key, value))
      : query.append(key, params[key]);
  });
  return query;
};

export const isSuperAdmin = (userRole) => {
  return userRole.indexOf(UserRolesEnum.SUPER_ADMIN) !== -1;
};

export const isUser = (userRole) => {
  return userRole.indexOf(UserRolesEnum.USER) !== -1;
};

export const isLabeler = (userRole) => {
  return userRole.indexOf(UserRolesEnum.LABELER) !== -1;
};

export const isUiPathUser = (userRole) => {
  return userRole.indexOf("uiPathUser") !== -1;
};

export const formatRoleName = (name) => {
  if (typeof name !== "string") throw Error("Role name must be string");
  return name.toLowerCase().replace(/_/g, " ").trim();
};

export const isHoya = (organization) => {
  return typeof organization === "string" && organization.toLowerCase().includes("hoya");
};

export const useQuery = () => {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
};

/**
 * @param {Array | Object} data
 */
export const castFormValues = (data) => {
  if (Array.isArray(data)) {
    return data.map((v) => (isNaN(+v) ? v : +v));
  } else if (typeof data === "object" && data !== null) {
    const result = {};
    Object.entries(data).forEach(([k, v]) => {
      result[k] = isNaN(+v) ? v : +v;
    });
    return result;
  }
};

/**
 * @param {string} url
 */
export const getParamsFromURL = (url = window.location.search) => {
  const urlSearchParams = new URLSearchParams(url);
  return Object.fromEntries(urlSearchParams.entries());
};

export const sortOptionsAlphabetically = (a, b) => a.value.localeCompare(b.value);

/**
 * @param {string[]} chunks
 */
export const buildURLPath = (chunks) => {
  return Array.isArray(chunks) ? `/${chunks.join("/")}` : "";
};

/**
 * @param {number} milliseconds
 */
export const getDateHoursMinutsSeconds = (milliseconds) => {
  const date = new Date(milliseconds);

  return {
    seconds: date.getUTCSeconds(),
    minutes: date.getUTCMinutes(),
    hours: date.getUTCHours(),
  };
};

/**
 * @param {number} milliseconds
 */
export const getDateInReadableFormat = (milliseconds, short = false) => {
  if (typeof milliseconds !== "number") return "";

  const { seconds, minutes, hours } = getDateHoursMinutsSeconds(milliseconds);

  let result = "";

  if (!seconds && !minutes && !hours) {
    const msRounded = Math.round(milliseconds);
    result += short ? `${msRounded}ms` : `${msRounded} milliseconds`;
  }

  if (hours) {
    result += short ? `${hours}h` : `${hours} hour` + (hours > 1 ? "s" : "");
  }
  if (minutes) {
    if (result.length > 0) result += ", ";
    result += short ? `${minutes}m` : `${minutes} minute` + (minutes > 1 ? "s" : "");
  }
  if (seconds) {
    if (result.length > 0) result += ", ";
    result += short ? `${seconds}s` : `${seconds} second` + (seconds > 1 ? "s" : "");
  }

  return result;
};

export const exportToJsonFile = (objectData, fileName) => {
  const json = JSON.stringify(objectData, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const downloadAnchorNode = document.createElement("a");
  downloadAnchorNode.setAttribute("href", url);
  downloadAnchorNode.setAttribute("download", fileName + ".json");
  document.body.appendChild(downloadAnchorNode);
  downloadAnchorNode.click();
  downloadAnchorNode.remove();

  URL.revokeObjectURL(url);
};